/**
 * Shared helpers for Sanity-driven forms and form wizards.
 *
 * Covers the whole form lifecycle EXCEPT display formatting (which lives in
 * `form-display.ts`, since it operates on already-validated, typed values):
 *
 *   1. Default values and value types shared between client and server
 *   2. Per-field validation (reused on both sides)
 *   3. Server-side type coercion from raw `FormData` → native types
 *   4. Convenience field-discovery utilities
 *
 * Everything here is side-effect-free and safe to import from both client
 * components and the server action.
 */

import {
  isValidPhoneNumber,
  parsePhoneNumberWithError,
} from "libphonenumber-js";

import type {
  FormConfigFromQuery,
  FormDataFromQuery,
  FormFieldFromQuery,
  FormWizardDataFromQuery,
} from "./types";

// ===========================================================================
// Value types
// ===========================================================================

/** A date-range value after server-side type coercion. */
export type DateRangeValue = { start: Date; end: Date };

/** A single row of a repeater field, keyed by column identifier. */
export type RepeaterRow = Record<string, string>;

export type FormFieldValue =
  | string
  | number
  | boolean
  | string[]
  | Date
  | DateRangeValue
  | RepeaterRow[]
  | null
  | undefined;

/** Record of user-submitted form values (strings, numbers, arrays from TanStack Form). */
export type FormValues = Record<string, FormFieldValue>;

/**
 * Shape of the second argument accepted by TanStack's `serverValidate`,
 * forwarded to `decode-formdata`. Kept local to avoid taking a direct
 * dependency on that transitive package.
 */
export type FormDataInfo = {
  arrays?: string[];
  booleans?: string[];
  dates?: string[];
  files?: string[];
  numbers?: string[];
};

/** A repeater column definition as projected from Sanity. */
export type RepeaterColumnConfig = {
  label?: string | null;
  columnName?: string | null;
  type?: string | null;
  required?: boolean | null;
};

/**
 * Normalise a repeater field value into row objects. Client-side the value is
 * already an array; server-side it arrives as a JSON string (the form encodes
 * the array of objects as JSON when building FormData).
 */
export function parseRepeaterValue(value: FormFieldValue): RepeaterRow[] {
  if (Array.isArray(value)) {
    return value.filter(
      (row): row is RepeaterRow =>
        typeof row === "object" && row !== null && !(row instanceof Date),
    );
  }
  if (typeof value === "string" && value.trim() !== "") {
    try {
      const parsed: unknown = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.filter(
          (row): row is RepeaterRow => typeof row === "object" && row !== null,
        );
      }
    } catch {
      // Malformed JSON — treat as no rows.
    }
  }
  return [];
}

/** Reads the `columns` config off a repeater field, tolerant of typegen drift. */
export function getRepeaterColumns(
  field: FormFieldFromQuery,
): RepeaterColumnConfig[] {
  const columns = (field as { columns?: RepeaterColumnConfig[] | null })
    .columns;
  return Array.isArray(columns) ? columns : [];
}

// ===========================================================================
// Defaults
// ===========================================================================

const defaultValueForFieldType = (
  field: Pick<FormFieldFromQuery, "type" | "multiple">,
): FormFieldValue => {
  switch (field.type) {
    case "number":
      return 0;
    case "checkbox":
      return [];
    case "multiSelect":
      return [];
    case "selectionCard" as FormFieldFromQuery["type"]:
      return false;
    case "file" as FormFieldFromQuery["type"]:
      // A multiple-file field holds an array of URLs; single holds one URL.
      return field.multiple ? [] : "";
    case "repeater" as FormFieldFromQuery["type"]:
      // A repeater holds an array of row objects keyed by column identifier.
      return [];
    case "text":
    case "email":
    case "phone":
    case "textarea":
    case "select":
    case "radio":
    case "date":
    case "dateRange":
    case "time":
    default:
      return "";
  }
};

/** A `formFieldGroups[]` item is either a dereferenced formFieldGroup (with
 *  `.fields`) or an inline `formTextBlock`. This guard narrows to groups so
 *  field iteration code can ignore standalone text blocks. */
const isFormFieldGroup = <T extends { _type?: string }>(
  item: T,
): item is Extract<T, { fields?: unknown }> =>
  (item as { _type?: string })._type !== "formTextBlock";

export const generateDefaultFormValues = (
  formData: FormDataFromQuery,
): Record<string, FormFieldValue> => {
  const defaultValues: Record<string, FormFieldValue> = {};
  formData.fieldGroups?.forEach((item) => {
    if (!isFormFieldGroup(item)) return;
    item.fields?.forEach((field) => {
      if (!field.fieldName) return;
      defaultValues[field.fieldName] = defaultValueForFieldType(field);
    });
  });
  return defaultValues;
};

/**
 * Flattens a form wizard's nested step/fieldGroup/field structure into a single
 * defaults object keyed by fieldName. TanStack Form holds one form instance
 * across all steps, so values from every step share one state tree.
 */
export const generateDefaultFormWizardValues = (
  wizardData: FormWizardDataFromQuery,
): Record<string, FormFieldValue> => {
  const defaultValues: Record<string, FormFieldValue> = {};
  wizardData.steps?.forEach((step) => {
    step.fieldGroups?.forEach((item) => {
      if (!isFormFieldGroup(item)) return;
      item.fields?.forEach((field) => {
        if (!field.fieldName) return;
        defaultValues[field.fieldName] = defaultValueForFieldType(field);
      });
    });
  });
  return defaultValues;
};

// ===========================================================================
// Per-field validation (shared client + server)
// ===========================================================================

export const validatePhoneNumber = (phone: string): string | undefined => {
  if (!phone || phone.trim() === "") {
    return undefined; // Let required validation handle empty values
  }

  try {
    // A leading "+" carries the real dial code, so libphonenumber-js can infer
    // the country itself — only fall back to South Africa for legacy/bare
    // values that were saved before the phone input started emitting "+".
    const defaultCountry = phone.trim().startsWith("+") ? undefined : "ZA";

    if (!isValidPhoneNumber(phone, defaultCountry)) {
      return "Please enter a valid phone number";
    }

    const parsedNumber = parsePhoneNumberWithError(phone, defaultCountry);
    if (!parsedNumber.isValid()) {
      return "Please enter a valid phone number";
    }

    return undefined;
  } catch {
    return "Please enter a valid phone number";
  }
};

export const commonValidation = (field: FormFieldFromQuery) => {
  return {
    onChange: ({ value }: { value: FormFieldValue }) => {
      // Handle boolean selection card — required means must be checked
      if ((field.type as string) === "selectionCard") {
        if (field.required && value !== true && value !== "true") {
          return `${field.label} is required`;
        }
        return undefined;
      }

      // Handle multi-value array fields (checkbox, multi-select dropdown)
      if (
        (field.type === "checkbox" || field.type === "multiSelect") &&
        field.required
      ) {
        if (Array.isArray(value)) {
          if (value.length === 0) return `${field.label} is required`;
          return undefined;
        }
        if (typeof value === "string") {
          if (value.trim() === "") return `${field.label} is required`;
          return undefined;
        }
        if (value == null) return `${field.label} is required`;
        return undefined;
      }

      // Handle phone validation first (before required check)
      if (field.type === "phone") {
        if (field.required) {
          const isEmpty =
            value == null ||
            (typeof value === "string" && value.trim() === "") ||
            (Array.isArray(value) && value.length === 0);
          if (isEmpty) return `${field.label} is required`;
        }
        if (value && value.toString().trim() !== "") {
          return validatePhoneNumber(value.toString());
        }
        return undefined;
      }

      // Handle email validation
      if (field.type === "email" && value && value.toString().trim() !== "") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value.toString())) {
          return "Please enter a valid email address";
        }
      }

      // Handle file URL validation — value is the public Vercel Blob URL, or an
      // array of URLs when the field allows multiple files.
      if ((field.type as string) === "file") {
        const urls = Array.isArray(value)
          ? value.filter((url): url is string => typeof url === "string")
          : typeof value === "string" && value.trim() !== ""
            ? [value]
            : [];

        if (field.required && urls.length === 0) {
          return `${field.label} is required`;
        }
        if (urls.some((url) => !/^https:\/\//.test(url))) {
          return "Uploaded file URL is invalid";
        }
        const maxFiles = (field as { maxFiles?: number | null }).maxFiles;
        if (typeof maxFiles === "number" && urls.length > maxFiles) {
          return `${field.label} accepts at most ${maxFiles} file${
            maxFiles === 1 ? "" : "s"
          }`;
        }
        return undefined;
      }

      // Handle repeater (repeatable list of rows). Value is an array client-side
      // and a JSON string server-side, so normalise before validating.
      if ((field.type as string) === "repeater") {
        const rows = parseRepeaterValue(value);
        if (field.required && rows.length === 0) {
          return `${field.label} requires at least one entry`;
        }
        const columns = getRepeaterColumns(field);
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        for (let index = 0; index < rows.length; index++) {
          const row = rows[index] ?? {};
          for (const column of columns) {
            if (!column?.columnName) continue;
            const cell = row[column.columnName];
            const isEmpty = cell == null || String(cell).trim() === "";
            const columnLabel = column.label || column.columnName;
            if (column.required && isEmpty) {
              return `${columnLabel} is required for entry ${index + 1}`;
            }
            if (column.type === "email" && !isEmpty && !emailRegex.test(cell)) {
              return `Please enter a valid email for ${columnLabel} in entry ${index + 1}`;
            }
          }
        }
        return undefined;
      }

      // Handle time validation
      if (field.type === "time") {
        if (field.required) {
          const isEmpty =
            value == null || (typeof value === "string" && value.trim() === "");
          if (isEmpty) return `${field.label} is required`;
        }
        if (value && value.toString().trim() !== "") {
          // 24-hour HH:MM (native <input type="time"> default output)
          if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(value.toString())) {
            return "Please enter a valid time";
          }
        }
        return undefined;
      }

      // Handle date-range validation (combined "YYYY-MM-DD/YYYY-MM-DD")
      if (field.type === "dateRange") {
        if (field.required) {
          const isEmpty =
            value == null || (typeof value === "string" && value.trim() === "");
          if (isEmpty) return `${field.label} is required`;
        }
        if (value && value.toString().trim() !== "") {
          const match = value
            .toString()
            .match(/^(\d{4}-\d{2}-\d{2})\/(\d{4}-\d{2}-\d{2})$/);
          if (!match) return "Please enter a valid date range";
          if (match[1] && match[2] && match[1] > match[2]) {
            return "Start date must be on or before end date";
          }
        }
        return undefined;
      }

      // Handle date validation
      if (field.type === "date") {
        if (field.required) {
          const isEmpty =
            value == null || (typeof value === "string" && value.trim() === "");
          if (isEmpty) return `${field.label} is required`;
        }
        if (value && value.toString().trim() !== "") {
          if (!/^\d{4}-\d{2}-\d{2}$/.test(value.toString())) {
            return "Please enter a valid date";
          }
        }
        return undefined;
      }

      // Handle required validation for other field types
      if (field.required) {
        const isEmpty =
          value == null ||
          (typeof value === "string" && value.trim() === "") ||
          (Array.isArray(value) && value.length === 0);
        if (isEmpty) return `${field.label} is required`;
      }

      // Handle character limit validation
      if (
        field.characterLimit &&
        value &&
        value.toString().length > field.characterLimit
      ) {
        return `${field.label} must be ${field.characterLimit} characters or less`;
      }

      return undefined;
    },
  };
};

// ===========================================================================
// Server-side: whole-form validation
// ===========================================================================

/** Reuses `commonValidation` for a single field. */
export function validateFormField(
  field: FormFieldFromQuery,
  value: FormFieldValue,
): string | undefined {
  const validation = commonValidation(field);
  return validation.onChange({ value });
}

/**
 * Walk the form config and validate every field against the submitted
 * values. Returns the first error message (matches TanStack's
 * single-error-per-form contract), or `undefined` if all fields pass.
 */
export function validateFormWithConfig(
  formData: FormValues,
  formConfig: FormConfigFromQuery | null,
): string | undefined {
  if (!formConfig?.fieldGroups) return undefined;

  const errors: string[] = [];
  formConfig.fieldGroups.forEach((item) => {
    if (!isFormFieldGroup(item)) return;
    item.fields?.forEach((field: FormFieldFromQuery) => {
      if (field.fieldName) {
        const fieldValue = formData[field.fieldName];
        const error = validateFormField(field, fieldValue);
        if (error) errors.push(error);
      }
    });
  });

  return errors.length > 0 ? errors[0] : undefined;
}

// ===========================================================================
// Server-side: type coercion from raw FormData
// ===========================================================================

/**
 * Parse our `YYYY-MM-DD` format into a local-midnight JS Date. Using local
 * (not UTC) keeps the calendar date visually stable for authors in any
 * timezone — "May 13" stays May 13 regardless of where they view it from.
 */
export function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1);
}

/**
 * Build the FormDataInfo used by TanStack's server validator (which delegates
 * to `decode-formdata`) to coerce raw FormData strings back to native types.
 *
 * - `selectionCard`, `newsletterCheckbox`, `popiConsentCheckbox` → boolean
 * - `number` → number
 * - `checkbox` / `multiSelect` (multi-value) → string[]
 * - everything else (text / email / phone / date / dateRange / time / …) → string
 *
 * Without this, FormData values land in MongoDB and webhooks as strings
 * like "true" / "42" because the HTTP wire format is untyped.
 */
export function buildFormDataInfo(
  formConfig: FormConfigFromQuery,
): FormDataInfo {
  const booleans: string[] = [];
  const numbers: string[] = [];
  const arrays: string[] = [];

  formConfig.fieldGroups?.forEach((item) => {
    if (!isFormFieldGroup(item)) return;
    item.fields?.forEach((field) => {
      if (!field.fieldName) return;
      const fieldType = field.type as string;
      if (
        fieldType === "selectionCard" ||
        fieldType === "newsletterCheckbox" ||
        fieldType === "popiConsentCheckbox"
      ) {
        booleans.push(field.fieldName);
      } else if (fieldType === "number") {
        numbers.push(field.fieldName);
      } else if (fieldType === "checkbox" || fieldType === "multiSelect") {
        arrays.push(field.fieldName);
      } else if (fieldType === "file" && field.multiple) {
        // Multiple-file fields submit one hidden input per URL under the same
        // name; mark them as arrays so decode-formdata rebuilds the list.
        arrays.push(field.fieldName);
      }
    });
  });

  return { booleans, numbers, arrays };
}

/**
 * After string-based validation, coerce `date` and `dateRange` field values
 * into native types so they land in Mongo as BSON Date / object and reach
 * webhooks as proper JSON types. Runs AFTER validation so the regex-based
 * checks in `commonValidation` still see string values.
 *
 * - `date`: "YYYY-MM-DD" → Date
 * - `dateRange`: "YYYY-MM-DD/YYYY-MM-DD" → { start: Date, end: Date }
 * - `time`: left as "HH:MM" string (no native Time type)
 * - booleans / numbers / arrays: already coerced by decode-formdata
 */
export function applyTypedTransforms(
  values: FormValues,
  formConfig: FormConfigFromQuery,
): FormValues {
  const out: FormValues = { ...values };
  formConfig.fieldGroups?.forEach((item) => {
    if (!isFormFieldGroup(item)) return;
    item.fields?.forEach((field) => {
      if (!field.fieldName) return;
      const raw = out[field.fieldName];
      const fieldType = field.type as string;

      if (fieldType === "repeater") {
        // The repeater arrives as a JSON string (single FormData entry).
        // Decode it to an array of row objects for storage and webhooks.
        out[field.fieldName] = parseRepeaterValue(raw);
      } else if (fieldType === "date") {
        if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
          out[field.fieldName] = parseIsoDate(raw);
        }
      } else if (fieldType === "dateRange") {
        if (typeof raw === "string") {
          const match = raw.match(/^(\d{4}-\d{2}-\d{2})\/(\d{4}-\d{2}-\d{2})$/);
          if (match && match[1] && match[2]) {
            out[field.fieldName] = {
              start: parseIsoDate(match[1]),
              end: parseIsoDate(match[2]),
            };
          }
        }
      }
    });
  });
  return out;
}

// ===========================================================================
// Field discovery
// ===========================================================================

/** Finds the single `newsletterCheckbox` field in a form config, if any. */
export function findNewsletterField(
  formConfig: FormConfigFromQuery,
): FormFieldFromQuery | null {
  if (!formConfig?.fieldGroups) return null;
  for (const group of formConfig.fieldGroups) {
    if (!isFormFieldGroup(group) || !group.fields) continue;
    for (const field of group.fields) {
      if (field.type === "newsletterCheckbox") return field;
    }
  }
  return null;
}

/** Finds the single `popiConsentCheckbox` field in a form config, if any. */
export function findPopiConsentField(
  formConfig: FormConfigFromQuery,
): FormFieldFromQuery | null {
  if (!formConfig?.fieldGroups) return null;
  for (const group of formConfig.fieldGroups) {
    if (!isFormFieldGroup(group) || !group.fields) continue;
    for (const field of group.fields) {
      if ((field.type as string) === "popiConsentCheckbox") return field;
    }
  }
  return null;
}

export function isNewsletterFieldChecked(
  formData: FormValues,
  newsletterField: FormFieldFromQuery,
): boolean {
  const fieldValue = formData[newsletterField.fieldName!];
  return fieldValue === true || fieldValue === "true";
}

/**
 * Looks up the submitter's email by finding the form's `type: "email"` field
 * and reading `formData[field.fieldName]`. Lets editors call the field anything
 * (`email`, `emailAddress`, etc.) without breaking server-side lookups.
 */
export function findEmailValue(
  formConfig: FormConfigFromQuery,
  formData: FormValues,
): string | undefined {
  if (!formConfig?.fieldGroups) return undefined;
  for (const group of formConfig.fieldGroups) {
    if (!isFormFieldGroup(group) || !group.fields) continue;
    for (const field of group.fields) {
      if (field.type === "email" && field.fieldName) {
        const value = formData[field.fieldName];
        if (typeof value === "string" && value) return value;
      }
    }
  }
  return undefined;
}

const BEONIC_PROFILE_FIELD_NAMES = [
  "firstName",
  "lastName",
  "phone",
  "gender",
  "relationship",
  "address",
  "suburb",
  "district",
  "state",
  "postcode",
  "country",
  "dobMin",
  "dobMax",
  "nationalId",
] as const;

type BeonicProfilePayload = {
  email: string;
} & Partial<Record<(typeof BEONIC_PROFILE_FIELD_NAMES)[number], string>>;

export function buildBeonicProfileFromForm(
  formConfig: FormConfigFromQuery,
  formData: FormValues,
  email: string,
): BeonicProfilePayload {
  const profile: BeonicProfilePayload = { email };
  if (!formConfig?.fieldGroups) return profile;

  for (const group of formConfig.fieldGroups) {
    if (!isFormFieldGroup(group) || !group.fields) continue;
    for (const field of group.fields) {
      const fieldName = field.fieldName;
      if (!fieldName) continue;
      if (
        !(BEONIC_PROFILE_FIELD_NAMES as readonly string[]).includes(fieldName)
      ) {
        continue;
      }
      const value = formData[fieldName];
      if (typeof value === "string" && value) {
        profile[fieldName as (typeof BEONIC_PROFILE_FIELD_NAMES)[number]] =
          value;
      }
    }
  }
  return profile;
}

export function isPopiConsentFieldChecked(
  formData: FormValues,
  popiConsentField: FormFieldFromQuery,
): boolean {
  const fieldValue = formData[popiConsentField.fieldName!];
  return fieldValue === true || fieldValue === "true";
}

/** Fallback wording when a `popiConsentCheckbox` has no authored label. */
export const DEFAULT_POPI_CONSENT_TEXT =
  "I consent to my submitted data being collected and stored";

/**
 * Resolve the exact consent wording a `popiConsentCheckbox` presents to the
 * user. Shared by the renderer and the submission handler so the persisted
 * consent snapshot always matches what the submitter actually agreed to.
 */
export function getPopiConsentText(field: FormFieldFromQuery): string {
  return field.options?.[0]?.label || field.label || DEFAULT_POPI_CONSENT_TEXT;
}
